<template>
  <div class="hms-page">
    <Navigation />
    <!-- HEADER -->
    <div class="hms-page-header">
      <div>
        <h1 class="hms-page-title">Medication Administration Record</h1>
        <p class="hms-page-sub">Bedside drug chart — record every dose given, withheld, refused or missed.</p>
      </div>
      <div class="hms-header-actions">
        <button class="hms-btn hms-btn-secondary" @click="loadAll">
          <span class="material-icons">refresh</span>Refresh
        </button>
      </div>
    </div>

    <!-- STATS -->
    <div class="hms-stats-row">
      <div class="hms-stat-card hms-stat-danger">
        <div class="hms-stat-label">Overdue</div>
        <div class="hms-stat-value">{{ stats.overdue || 0 }}</div>
        <div class="hms-stat-foot">scheduled but not yet given</div>
      </div>
      <div class="hms-stat-card hms-stat-warn">
        <div class="hms-stat-label">Due in next hour</div>
        <div class="hms-stat-value">{{ stats.due_next_hour || 0 }}</div>
        <div class="hms-stat-foot">upcoming doses</div>
      </div>
      <div class="hms-stat-card hms-stat-success">
        <div class="hms-stat-label">Given today</div>
        <div class="hms-stat-value">{{ stats.given_today || 0 }}</div>
      </div>
      <div class="hms-stat-card hms-stat-warn">
        <div class="hms-stat-label">Late today</div>
        <div class="hms-stat-value">{{ stats.late_today || 0 }}</div>
      </div>
      <div class="hms-stat-card">
        <div class="hms-stat-label">Not given today</div>
        <div class="hms-stat-value">{{ stats.not_given_today || 0 }}</div>
        <div class="hms-stat-foot">withheld / refused / missed</div>
      </div>
    </div>

    <!-- TABS -->
    <div class="hms-tabs">
      <div class="hms-tab" :class="{ active: tab === 'now' }" @click="tab = 'now'">
        <span class="material-icons">schedule</span>Now Due
        <span v-if="dueRows.length" class="hms-tab-pill">{{ dueRows.length }}</span>
      </div>
      <div class="hms-tab" :class="{ active: tab === 'patient' }" @click="tab = 'patient'">
        <span class="material-icons">person</span>Patient Chart
      </div>
    </div>

    <!-- =========== NOW DUE TAB =========== -->
    <div v-if="tab === 'now'">
      <div v-if="!dueRows.length" class="hms-empty">
        <span class="material-icons">check_circle</span>
        <h3>All caught up</h3>
        <p>No doses are overdue or due in the next hour.</p>
      </div>

      <div v-else class="hms-mar-list">
        <div v-for="row in dueRows" :key="row.mar_id" class="hms-mar-card" :class="{ overdue: row.is_late == 1 }">
          <div class="hms-mar-time">
            <div class="hms-mar-clock">{{ formatTime(row.scheduled_at) }}</div>
            <div v-if="row.is_late == 1" class="hms-mar-late">{{ Math.abs(row.minutes_until) }} min late</div>
            <div v-else class="hms-mar-soon">in {{ row.minutes_until }} min</div>
          </div>
          <div class="hms-mar-body">
            <div class="hms-mar-drug">
              {{ row.drug_name }}
              <span v-if="row.is_controlled == 1" class="hms-pill hms-pill-danger">CD</span>
            </div>
            <div class="hms-mar-dose">{{ row.dose }} · {{ row.route || '—' }}</div>
            <div class="hms-mar-pt">
              <strong>{{ row.patient_name }}</strong>
              <span v-if="row.patient_no"> ({{ row.patient_no }})</span>
              <span v-if="row.ward_name"> — {{ row.ward_name }}</span>
              <span v-if="row.bed_number"> · Bed {{ row.bed_number }}</span>
            </div>
            <div v-if="row.allergies" class="hms-mar-allergy">
              <span class="material-icons">warning</span>Allergies: {{ row.allergies }}
            </div>
          </div>
          <div class="hms-mar-actions">
            <button class="hms-btn hms-btn-success" @click="openChart(row, 'given')">
              <span class="material-icons">check</span>Given
            </button>
            <button class="hms-btn hms-btn-secondary" @click="openChart(row, 'withheld')">Withhold</button>
            <button class="hms-btn hms-btn-secondary" @click="openChart(row, 'refused')">Refused</button>
            <button class="hms-btn hms-btn-danger" @click="openChart(row, 'missed')">Missed</button>
          </div>
        </div>
      </div>
    </div>

    <!-- =========== PATIENT CHART TAB =========== -->
    <div v-if="tab === 'patient'">
      <div class="hms-card">
        <div class="hms-card-row">
          <input v-model="patientFilter" class="hms-input" placeholder="Search patient by name or number" @keyup.enter="searchPatient" />
          <button class="hms-btn hms-btn-primary" @click="searchPatient">Search</button>
        </div>

        <div v-if="!ptDoses.length" class="hms-muted hms-pad">
          {{ patientFilter ? 'No doses on record for that patient.' : 'Search for a patient to view their MAR.' }}
        </div>

        <table v-else class="hms-table">
          <thead>
            <tr>
              <th>Scheduled</th>
              <th>Drug</th>
              <th>Dose / Route</th>
              <th>Status</th>
              <th>Given At</th>
              <th>By</th>
              <th>Witness</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="d in ptDoses" :key="d.mar_id">
              <td>{{ formatDateTime(d.scheduled_at) }}</td>
              <td>
                {{ d.drug_name }}
                <span v-if="d.is_controlled == 1" class="hms-pill hms-pill-danger">CD</span>
              </td>
              <td>{{ d.dose }} · {{ d.route || '—' }}</td>
              <td><span class="hms-pill" :class="statusClass(d.status)">{{ d.status }}</span></td>
              <td>{{ d.administered_at ? formatDateTime(d.administered_at) : '—' }}</td>
              <td>{{ d.administered_by_name || '—' }}</td>
              <td>{{ d.witnessed_by_name || '—' }}</td>
              <td>{{ d.reason || d.notes || '' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- =========== CHART DOSE MODAL =========== -->
    <div v-if="chartOpen" class="hms-modal-bg" @click.self="chartOpen = false">
      <div class="hms-modal">
        <div class="hms-modal-head">
          <h3>{{ chartLabel(chartStatus) }} — {{ chartRow.drug_name }}</h3>
          <button class="hms-icon-btn" @click="chartOpen = false">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div class="hms-modal-body">
          <div class="hms-info-row">
            <div><strong>Patient:</strong> {{ chartRow.patient_name }}</div>
            <div><strong>Dose:</strong> {{ chartRow.dose }} {{ chartRow.route ? '· ' + chartRow.route : '' }}</div>
            <div><strong>Scheduled:</strong> {{ formatDateTime(chartRow.scheduled_at) }}</div>
          </div>

          <div v-if="chartRow.is_controlled == 1 && chartStatus === 'given'" class="hms-warn-banner">
            <span class="material-icons">verified_user</span>
            Controlled drug — a second nurse must witness and co-sign.
          </div>

          <div v-if="chartStatus !== 'given'" class="hms-form-group">
            <label>Reason <span class="hms-required">*</span></label>
            <select v-model="chartReason" class="hms-input">
              <option value="">— select —</option>
              <option v-for="r in reasonOptions(chartStatus)" :key="r" :value="r">{{ r }}</option>
            </select>
          </div>

          <div v-if="chartRow.is_controlled == 1 && chartStatus === 'given'" class="hms-form-group">
            <label>Witnessing nurse <span class="hms-required">*</span></label>
            <select v-model="chartWitness" class="hms-input">
              <option value="">— select witness —</option>
              <option v-for="u in witnesses" :key="u.user_id" :value="u.user_id">
                {{ u.first_name }} {{ u.last_name }} ({{ u.role_name || u.staff_no }})
              </option>
            </select>
          </div>

          <div class="hms-form-group">
            <label>Notes</label>
            <textarea v-model="chartNotes" class="hms-input" rows="2" placeholder="Site, response, observations…"></textarea>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="chartOpen = false">Cancel</button>
          <button class="hms-btn" :class="chartStatus === 'given' ? 'hms-btn-success' : 'hms-btn-primary'" @click="confirmChart" :disabled="charting">
            {{ charting ? 'Saving…' : ('Confirm ' + chartLabel(chartStatus)) }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

export default {
  name: "MedicationAdministration",
  components: { Navigation },
  data() {
    return {
      tab: 'now',
      stats: {},
      dueRows: [],

      patientFilter: '',
      ptDoses: [],
      foundPatientId: null,

      chartOpen: false,
      chartRow: {},
      chartStatus: 'given',
      chartReason: '',
      chartNotes: '',
      chartWitness: '',
      charting: false,

      witnesses: [],

      pollHandle: null
    };
  },
  mounted() {
    this.loadDue();
    this.loadWitnesses();
    this.pollHandle = setInterval(this.loadDue, 60 * 1000);
  },
  beforeUnmount() {
    if (this.pollHandle) clearInterval(this.pollHandle);
  },
  methods: {
    async loadAll() { await this.loadDue(); },
    async loadDue() {
      try {
        const r = await executeJson({ function: 'getMarDueNow' }, hms);
        if (r.data && r.data.success) {
          const d = r.data.data || {};
          this.dueRows = d.Due   || [];
          this.stats   = d.Stats || {};
        }
      } catch (e) { console.error(e); }
    },
    async loadWitnesses() {
      try {
        const r = await executeJson({ function: 'getStaff', role_filter: 'Nurse' }, hms);
        if (r.data && r.data.success) {
          const d = r.data.data || {};
          this.witnesses = d.Staff || d.staff || [];
        }
      } catch (e) { /* not fatal */ }
    },

    async searchPatient() {
      const q = (this.patientFilter || '').trim();
      if (!q) return;
      try {
        const r = await executeJson({ function: 'getPatients', search: q, limit: 1 }, hms);
        if (!r.data || !r.data.success) { alert('Search failed.'); return; }
        const list = (r.data.data && (r.data.data.Patients || r.data.data.patients)) || [];
        const p = list[0];
        if (!p) { alert('No patient found.'); this.ptDoses = []; return; }
        this.foundPatientId = p.patient_id;
        const m = await executeJson({ function: 'getMarForPatient', patient_id: p.patient_id }, hms);
        if (m.data && m.data.success) {
          this.ptDoses = (m.data.data && m.data.data.Doses) || [];
        }
      } catch (e) { console.error(e); alert('Search failed.'); }
    },

    openChart(row, status) {
      this.chartRow     = row;
      this.chartStatus  = status;
      this.chartReason  = '';
      this.chartNotes   = '';
      this.chartWitness = '';
      this.chartOpen    = true;
    },
    chartLabel(s) {
      return ({ given: 'Mark Given', withheld: 'Withhold', refused: 'Refused', missed: 'Mark Missed' })[s] || s;
    },
    reasonOptions(s) {
      if (s === 'withheld') return ['NPO / fasting', 'Vital signs out of range', 'Awaiting lab result', 'Doctor on review', 'Drug not available', 'Other'];
      if (s === 'refused')  return ['Patient declined', 'Adverse effect last dose', 'Religious / cultural', 'Other'];
      if (s === 'missed')   return ['Patient off-ward (theatre/imaging)', 'Patient absconded', 'Drug unavailable', 'Documentation gap', 'Other'];
      return [];
    },
    statusClass(s) {
      return ({
        scheduled: 'hms-pill-info',
        given:     'hms-pill-success',
        late:      'hms-pill-warn',
        withheld:  'hms-pill-warn',
        refused:   'hms-pill-warn',
        missed:    'hms-pill-danger',
        cancelled: 'hms-pill-muted'
      })[s] || '';
    },

    async confirmChart() {
      if (this.chartStatus !== 'given' && !this.chartReason) { alert('Please select a reason.'); return; }
      if (this.chartRow.is_controlled == 1 && this.chartStatus === 'given' && !this.chartWitness) {
        alert('A witnessing nurse is required for controlled drugs.'); return;
      }
      this.charting = true;
      try {
        const r = await executeJson({
          function: 'chartDose',
          mar_id:        this.chartRow.mar_id,
          status:        this.chartStatus,
          reason:        this.chartReason,
          notes:         this.chartNotes,
          witnessed_by:  this.chartWitness
        }, hms);
        if (r.data && r.data.success) {
          this.chartOpen = false;
          await this.loadDue();
        } else {
          alert((r.data && r.data.message) || 'Could not save dose.');
        }
      } catch (e) { console.error(e); alert('Failed to record dose.'); }
      finally { this.charting = false; }
    },

    formatTime(dt) {
      if (!dt) return '';
      const d = new Date(dt.replace ? dt.replace(' ', 'T') : dt);
      return isNaN(d) ? dt : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    },
    formatDateTime(dt) {
      if (!dt) return '';
      const d = new Date(dt.replace ? dt.replace(' ', 'T') : dt);
      return isNaN(d) ? dt : d.toLocaleString([], { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' });
    }
  }
};
</script>

<style scoped>
.hms-page { padding: 24px; max-width: 1400px; margin: 0 auto; }
.hms-page-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 20px; }
.hms-page-title { font-size: 24px; font-weight: 700; margin: 0; color: #1a202c; }
.hms-page-sub { color: #718096; margin-top: 4px; font-size: 14px; }
.hms-header-actions { display: flex; gap: 8px; }

.hms-stats-row { display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px; margin-bottom: 20px; }
.hms-stat-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 14px; }
.hms-stat-card.hms-stat-danger  { border-left: 4px solid #e53e3e; }
.hms-stat-card.hms-stat-warn    { border-left: 4px solid #dd6b20; }
.hms-stat-card.hms-stat-success { border-left: 4px solid #38a169; }
.hms-stat-label { font-size: 12px; color: #718096; text-transform: uppercase; letter-spacing: .5px; }
.hms-stat-value { font-size: 28px; font-weight: 700; color: #1a202c; margin-top: 4px; }
.hms-stat-foot  { font-size: 11px; color: #a0aec0; margin-top: 2px; }

.hms-tabs { display: flex; gap: 8px; border-bottom: 1px solid #e2e8f0; margin-bottom: 16px; }
.hms-tab { padding: 10px 16px; cursor: pointer; display: flex; align-items: center; gap: 6px; font-size: 14px; color: #718096; border-bottom: 2px solid transparent; }
.hms-tab.active { color: #2b6cb0; border-bottom-color: #2b6cb0; font-weight: 600; }
.hms-tab .material-icons { font-size: 18px; }
.hms-tab-pill { background: #e53e3e; color: #fff; border-radius: 10px; padding: 0 8px; font-size: 11px; font-weight: 700; }

.hms-mar-list { display: flex; flex-direction: column; gap: 10px; }
.hms-mar-card { background: #fff; border: 1px solid #e2e8f0; border-left: 4px solid #4299e1; border-radius: 8px; padding: 14px; display: grid; grid-template-columns: 110px 1fr auto; gap: 16px; align-items: center; }
.hms-mar-card.overdue { border-left-color: #e53e3e; background: #fff5f5; }
.hms-mar-time { text-align: center; }
.hms-mar-clock { font-size: 22px; font-weight: 700; color: #2d3748; }
.hms-mar-late  { color: #c53030; font-size: 12px; font-weight: 600; }
.hms-mar-soon  { color: #4299e1; font-size: 12px; }
.hms-mar-drug  { font-size: 16px; font-weight: 600; color: #1a202c; display: flex; align-items: center; gap: 6px; }
.hms-mar-dose  { color: #4a5568; font-size: 13px; }
.hms-mar-pt    { font-size: 13px; color: #2d3748; margin-top: 4px; }
.hms-mar-allergy { color: #c53030; font-size: 12px; margin-top: 4px; display: flex; align-items: center; gap: 4px; }
.hms-mar-allergy .material-icons { font-size: 14px; }
.hms-mar-actions { display: flex; gap: 6px; flex-wrap: wrap; justify-content: flex-end; }

.hms-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 16px; }
.hms-card-row { display: flex; gap: 8px; margin-bottom: 12px; }
.hms-input { flex: 1; padding: 8px 12px; border: 1px solid #cbd5e0; border-radius: 6px; font-size: 14px; }
.hms-pad { padding: 24px; text-align: center; }
.hms-muted { color: #a0aec0; }

.hms-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.hms-table th { background: #f7fafc; text-align: left; padding: 10px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #4a5568; }
.hms-table td { padding: 10px; border-bottom: 1px solid #edf2f7; }

.hms-pill { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; text-transform: uppercase; }
.hms-pill-success { background: #c6f6d5; color: #22543d; }
.hms-pill-info    { background: #bee3f8; color: #2a4365; }
.hms-pill-warn    { background: #feebc8; color: #7b341e; }
.hms-pill-danger  { background: #fed7d7; color: #742a2a; }
.hms-pill-muted   { background: #edf2f7; color: #4a5568; }

.hms-empty { background: #fff; border: 1px dashed #cbd5e0; border-radius: 8px; padding: 48px; text-align: center; color: #718096; }
.hms-empty .material-icons { font-size: 48px; color: #38a169; }
.hms-empty h3 { margin: 8px 0 4px; color: #2d3748; }

.hms-btn { display: inline-flex; align-items: center; gap: 6px; padding: 8px 14px; border-radius: 6px; border: 1px solid transparent; font-size: 13px; font-weight: 500; cursor: pointer; }
.hms-btn .material-icons { font-size: 16px; }
.hms-btn-primary   { background: #2b6cb0; color: #fff; }
.hms-btn-secondary { background: #fff; color: #2d3748; border-color: #cbd5e0; }
.hms-btn-success   { background: #38a169; color: #fff; }
.hms-btn-danger    { background: #e53e3e; color: #fff; }

.hms-modal-bg { position: fixed; inset: 0; background: rgba(0,0,0,.5); display: flex; align-items: center; justify-content: center; z-index: 100; }
.hms-modal { background: #fff; border-radius: 8px; width: 540px; max-width: 95vw; max-height: 90vh; display: flex; flex-direction: column; }
.hms-modal-head { padding: 16px 20px; border-bottom: 1px solid #e2e8f0; display: flex; justify-content: space-between; align-items: center; }
.hms-modal-head h3 { margin: 0; font-size: 16px; }
.hms-modal-body { padding: 16px 20px; overflow: auto; }
.hms-modal-foot { padding: 12px 20px; border-top: 1px solid #e2e8f0; display: flex; justify-content: flex-end; gap: 8px; }
.hms-icon-btn { background: none; border: none; cursor: pointer; }

.hms-info-row { background: #f7fafc; padding: 10px; border-radius: 6px; margin-bottom: 12px; font-size: 13px; display: grid; gap: 4px; }
.hms-warn-banner { background: #fffaf0; border: 1px solid #f6ad55; color: #7b341e; padding: 10px; border-radius: 6px; margin-bottom: 12px; display: flex; align-items: center; gap: 8px; font-size: 13px; }
.hms-form-group { margin-bottom: 12px; }
.hms-form-group label { display: block; font-size: 12px; color: #4a5568; margin-bottom: 4px; font-weight: 600; }
.hms-required { color: #e53e3e; }
</style>
